import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { captureMessage } from '@sentry/browser';
import defaultAxios from 'axios';
import { t } from "i18next";
import axios from "../../config/axios";
import { customAxiosApi, globalConfig } from "../../config/axios-retry";
import Toast from "../../shared/toast/Toast";
import { LOOKUPS_URL } from '../../utils/helpers/utils';
import { ResponseResult } from "../../utils/interfaces/http/ResponseResult";
import { PartnerProfileStore } from "../../utils/interfaces/partner/Partner.store";
import { PartnerCompanyInfoUpdate } from '../../utils/interfaces/partner/PartnerCompanyInfoUpdate';
import { PartnerRegistration } from "../../utils/interfaces/partner/PartnerRegistration";
import { RegistrationLookup } from "../../utils/interfaces/partner/RegistrationLookup";
import { VATInfoInterface } from "../../utils/interfaces/partner/VATInfo";
import { FULLTIME_PARTNER_ID_KEY, PARTNER_STATUS, PARTNER_STATUS_MODAL_SHOWN, USER_LOCATION_COORDS } from './../../utils/constants/constants';
import { RoleEnum } from "src/utils/enums/EnterpriseEnums";
import { Partner, PartnerProfile, PartnerUser } from "src/utils/interfaces/partner/PartnerProfile";
import { CompanySizeEnum } from "src/utils/enums/CompanySizeEnum";
import { ListApplicationStagesItem, UpdateApplicationStagesBody } from "src/utils/interfaces/job/Job.store";

const appKey = 'appPartner'
const apiBaseUrl = 'pa/ft/partners'
const apiModuleUrl = `${apiBaseUrl}/me`;
const apiStatusUrl = `${apiBaseUrl}/status`;
const apiRegistrationLookups = `${apiBaseUrl}/registration-lookups`;
const apiPartnerRegistrationUrl = `${apiBaseUrl}/register`;
const apiVATUrl = `${apiBaseUrl}/vat-info`;
const apiCompanyProfileUpdateUrl = `${apiBaseUrl}/company-info`;
const apiSendEmailVerificationUrl = `ft/users/email/resend-verification`;
const apiUpdateCompanyLegalInfoUrl = `${apiBaseUrl}/update-company-legal-info`;
const apiUpdatePUNameUrl = `${apiBaseUrl}/update-partner-user-name`;
const apiDynamicStagesUrl = `pa/ft/partner-application-stages`;


export const getPartnerInfo = createAsyncThunk(`${appKey}/getPartnerInfo`, async (): Promise<any> => {
    const response = await customAxiosApi.get(`${apiModuleUrl}`, globalConfig);
    const resData = response.data;
    // TEMP: force loyal customer for local testing
    // const partners = (resData?.partners || []).map((p: Partner, i: number) =>
    //     i === 0 ? { ...p, isLoyalCustomer: true } : p
    // );
    // return { ...resData, partners };
    return { ...resData };
})

export const listActivePartners = createAsyncThunk(`${appKey}/listActivePartners`, async (): Promise<any> => {
    const response = await customAxiosApi.get(`${apiBaseUrl}/list-active`, globalConfig);
    const resData = response.data;
    return resData;
})


export const getPartnerStatus = createAsyncThunk(`${appKey}/getPartnerStatus`, async () => {
    const response = await customAxiosApi.get(`${apiStatusUrl}`, globalConfig);
    return response.data.data;
})

export const getPartnerRegistrationLookups = createAsyncThunk(`${appKey}/getPartnerRegistrationLookups`, async () => {
    const response = await customAxiosApi.get<ResponseResult<RegistrationLookup>>(`${apiRegistrationLookups}`, globalConfig);
    return response.data.data;
})

export const getPaLookups = createAsyncThunk(`${appKey}/getPaLookups`, async () => {
    const response = await defaultAxios.get(`${LOOKUPS_URL()}`, { ...globalConfig, headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache', 'Expires': '0' } });
    if (response?.headers && response.headers['user-lat'] && response.headers['user-lng']) {
        localStorage.setItem(USER_LOCATION_COORDS, JSON.stringify({ userLat: response.headers['user-lat'], userLng: response.headers['user-lng'], userCountry: response.headers['user-country'] }));
    }

    return response.data.pa;
})

export const registerPartner = createAsyncThunk(`${appKey}/registerPartner`, async (body: PartnerRegistration, { dispatch }) => {
    const response = await customAxiosApi.post<ResponseResult<any>>(`${apiPartnerRegistrationUrl}`, { ...body }, globalConfig);
    return response.data.data;
})

export const addPartnerVATInfo = createAsyncThunk(`${appKey}/addPartnerVATInfo`, async (body: VATInfoInterface, { dispatch }) => {
    const response = await axios.put<ResponseResult<any>>(`${apiVATUrl}`, body);
    if (response.status === 200) {
        dispatch(getPartnerInfo())
    }
    return response.data.data;
})

export const updateCompanyProfile = createAsyncThunk(`${appKey}/updateCompanyProfile`, async (body: any, { dispatch }) => {
    const response = await axios.put<ResponseResult<any>>(`${apiCompanyProfileUpdateUrl}`, { ...body });
    if (response.status === 200) {
        dispatch(getPartnerInfo())
    }
    return response.data.data;
})

export const completeCompanyOnboarding = createAsyncThunk(`${appKey}/completeCompanyOnboarding`, async (body: PartnerCompanyInfoUpdate, { dispatch }) => {
    const response = await axios.put<ResponseResult<any>>(`${apiCompanyProfileUpdateUrl}`, { ...body });
    if (response.status === 200) {
        dispatch(getPartnerInfo())
    }
    return response.data.data;
})

export const sendEmailVerification = createAsyncThunk(`${appKey}/sendEmailVerification`, async (body: any, { dispatch }) => {
    const response = await axios.get<ResponseResult<any>>(`${apiSendEmailVerificationUrl}`);
    if (response.status === 200) {
        dispatch(getPartnerInfo())
    }
    return response.data.data;
})


export const updateCompanyLegalInfo = createAsyncThunk(`${appKey}/updateCompanyLegalInfo`, async (body: any, { dispatch }) => {
    const response = await axios.put<ResponseResult<any>>(`${apiUpdateCompanyLegalInfoUrl}`, { ...body });
    if (response.status === 200) {
        dispatch(getPartnerInfo())
    }
    return response.data.data;
})

export const updatePartnerUserName = createAsyncThunk(`${appKey}/updatePartnerUserName`, async (body: any, { dispatch }) => {
    const response = await axios.put<ResponseResult<any>>(`${apiUpdatePUNameUrl}`, { ...body });
    if (response.status === 200) {
        dispatch(getPartnerInfo())
    }
    return response.data.data;
})

export const completeWhatsappSetup = createAsyncThunk(`${appKey}/completeWhatsappSetup`, async (body: { code: string }, { dispatch }) => {
    const response = await axios.post<ResponseResult<any>>(`${apiBaseUrl}/setup-whatsapp-config`, body);
    return response.data.data;
})

export const completeWhatsappSetupV2 = createAsyncThunk(`${appKey}/completeWhatsappSetupV2`, async (body: { code: string; metaPhoneNumberId: string; metaWabaId: string; metaBusinessId: string }, { dispatch }) => {
    const response = await axios.post<ResponseResult<any>>(`${apiBaseUrl}/setup-whatsapp-config-v2`, body);
    return response.data.data;
})

export const deactivateWhatsappSetup = createAsyncThunk(`${appKey}/deactivateWhatsappSetup`, async () => {
    const response = await axios.post<ResponseResult<any>>(`${apiBaseUrl}/revoke-whatsapp-config`);
    return response.data.data;
})

export const getPartnerDynamicStages = createAsyncThunk(`${appKey}/getPartnerDynamicStages`, async () => {
    const response = await customAxiosApi.get(`${apiDynamicStagesUrl}/list`, globalConfig);
    return response.data.data as ListApplicationStagesItem[];
})

export const upsertPartnerDynamicStages = createAsyncThunk(`${appKey}/upsertPartnerDynamicStages`, async (body: UpdateApplicationStagesBody, { dispatch }) => {
    const response = await customAxiosApi.put(`${apiDynamicStagesUrl}/upsert-stages`, body, globalConfig);
    // Refetch so newly created stages get real server IDs (clears client-side isNew)
    await dispatch(getPartnerDynamicStages());
    return response.data.data;
})

export const deletePartnerDynamicStage = createAsyncThunk(`${appKey}/deletePartnerDynamicStage`, async (id: string) => {
    const response = await customAxiosApi.delete(`${apiDynamicStagesUrl}/${id}`, globalConfig);
    return response.data.data;
})

export const setRemainingApplicantCvViewsPerResetCycle = createAction<number>(`${appKey}/setRemainingApplicantCvViewsPerResetCycle`);
export const setRemainingVideoInterviewViewsPerResetCycle = createAction<number>(`${appKey}/setRemainingVideoInterviewViewsPerResetCycle`);
export const setRemainingInterviewsPerResetCycle = createAction<number>(`${appKey}/setRemainingInterviewsPerResetCycle`);
export const setRemainingUsersPerResetCycle = createAction<number>(`${appKey}/setRemainingUsersPerResetCycle`);
export const setUnlocksRemainingPerResetCycle = createAction<number>(`${appKey}/setUnlocksRemainingPerResetCycle`);


const initialState: PartnerProfileStore = {
    isLoading: true,
    partnerProfile: null,
    activePartners: [],
    lookup: null,
    showCompanyProfilePopup: false,
    showInCompleteCompanyProfileMPopup: false,
    balances: {},
    isLoadingPartnerDynamicStages: false,
    isLoadingUpsertPartnerDynamicStages: false,
    isLoadingDeletePartnerDynamicStages: false,
    partnerDynamicStages: []
}

export const appPartnerSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {
        handleCompanyProfileModal: (state, action) => {
            state.showCompanyProfilePopup = action.payload
        },
        handleInCompleteCompanyProfileModal: (state, action) => {
            state.showInCompleteCompanyProfileMPopup = action.payload
        }
    },
    extraReducers: builder => {
        builder
            .addCase(getPartnerInfo.fulfilled, (state, action) => {
                const partnerProfile: PartnerProfile = action.payload
                state.partnerProfile = partnerProfile;
                const partner = partnerProfile?.partners?.[0];
                const lsPartnerStatus = localStorage.getItem(PARTNER_STATUS);
                if (lsPartnerStatus === null) {
                    localStorage.setItem(PARTNER_STATUS, partner.status);
                    localStorage.setItem(PARTNER_STATUS_MODAL_SHOWN, 'false');
                }

                state.balances = partnerProfile?.partners?.[0]?.balancesAndLimits;
            })
            .addCase(getPartnerRegistrationLookups.fulfilled, (state, action) => {
                state.lookup = action.payload;
            })
            .addCase(addPartnerVATInfo.fulfilled, (state, action) => {
                // Toast.Success({ content: t('vat_info_confirmation_message') })
            })
            .addCase(listActivePartners.fulfilled, (state, action) => {
                state.activePartners = action.payload?.data;
            })
            .addCase(addPartnerVATInfo.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message })
            })
            .addCase(updateCompanyProfile.fulfilled, (state, action) => {
                Toast.Success({ content: t('company_profile_updated') })
            })
            .addCase(updateCompanyProfile.rejected, (state, action) => {
                let errorMessage = action?.error?.message;
                if (errorMessage === 'Unprocessable Entity') errorMessage = t('not_allowed');
                Toast.Error({ content: errorMessage });
                // setTimeout(() => {
                //     window.location.href = '/#/jobs';
                //     window.location.reload();
                // }, 5000);
            })
            .addCase(completeCompanyOnboarding.fulfilled, (state, action) => {
                Toast.Success({ content: t('completed_registration_confirmation_message') })
            })
            .addCase(completeCompanyOnboarding.rejected, (state, action) => {
                let errorMessage = action?.error?.message;
                if (errorMessage === 'Unprocessable Entity') errorMessage = t('not_allowed');
                Toast.Error({ content: errorMessage });
                setTimeout(() => {
                    window.location.href = '/#/jobs';
                    window.location.reload();
                }, 5000);
            })
            .addCase(registerPartner.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message || t('ERR_BAD_REQUEST') })
            })
            .addCase(sendEmailVerification.fulfilled, (state, action) => {
                Toast.Success({ content: t('email_verification_sent') })
            })
            .addCase(sendEmailVerification.rejected, (state, action) => {
                let errorMessage = action?.error?.message;
                if (errorMessage === 'Invalid Request') errorMessage = t('email_already_verified');
                Toast.Error({ content: errorMessage });
            })
            .addCase(updateCompanyLegalInfo.fulfilled, (state, action) => {
                Toast.Success({ content: t('updated_company_legal_info') })
            })
            .addCase(updateCompanyLegalInfo.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message })
            })
            .addCase(updatePartnerUserName.fulfilled, (state, action) => {
                Toast.Success({ content: t('updated_partner_user_name_message') })
            })
            .addCase(updatePartnerUserName.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.code })
            })
            .addCase(completeWhatsappSetup.fulfilled, (state, action) => {
                Toast.Success({ content: t('completed_whatsapp_setup_message') })
            })
            .addCase(completeWhatsappSetup.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message })
            })
            .addCase(completeWhatsappSetupV2.fulfilled, (state, action) => {
                Toast.Success({ content: t('completed_whatsapp_setup_message') })
            })
            .addCase(completeWhatsappSetupV2.rejected, (state, action) => {
                Toast.Error({ content: action?.error?.message })
            })
            .addCase(setRemainingApplicantCvViewsPerResetCycle, (state, action) => {
                const newAmount = action.payload;
                state.balances.remainingApplicantCvViewsPerResetCycle = newAmount <= 0 ? 0 : newAmount;
            })
            .addCase(setRemainingVideoInterviewViewsPerResetCycle, (state, action) => {
                const newAmount = action.payload;
                state.balances.remainingVideoInterviewViewsPerResetCycle = newAmount <= 0 ? 0 : newAmount;
            })
            .addCase(setRemainingInterviewsPerResetCycle, (state, action) => {
                const newAmount = action.payload;
                state.balances.remainingInterviewsPerResetCycle = newAmount <= 0 ? 0 : newAmount;
            })
            .addCase(setRemainingUsersPerResetCycle, (state, action) => {
                const newAmount = action.payload;
                state.balances.usersRemainingPerResetCycle = newAmount <= 0 ? 0 : newAmount;
            })
            .addCase(setUnlocksRemainingPerResetCycle, (state, action) => {
                const newAmount = action.payload;
                state.balances.unlocksRemainingPerResetCycle = newAmount <= 0 ? 0 : newAmount;
            })
            .addCase(getPartnerDynamicStages.pending, (state, action) => {
                // Avoid full-list spinner flash when refreshing after upsert
                if (state.partnerDynamicStages.length === 0) {
                    state.isLoadingPartnerDynamicStages = true;
                }
            })
            .addCase(getPartnerDynamicStages.fulfilled, (state, action) => {
                state.partnerDynamicStages = action.payload;
                state.isLoadingPartnerDynamicStages = false;
            })
            .addCase(getPartnerDynamicStages.rejected, (state, action) => {
                state.isLoadingPartnerDynamicStages = false;
                Toast.Error({ content: action?.error?.message })
            })
            .addCase(upsertPartnerDynamicStages.pending, (state, action) => {
                state.isLoadingUpsertPartnerDynamicStages = true;
            })
            .addCase(upsertPartnerDynamicStages.fulfilled, (state, action) => {
                state.isLoadingUpsertPartnerDynamicStages = false;
                Toast.Success({ content: t('dynamic_stages_updated') })
            })
            .addCase(upsertPartnerDynamicStages.rejected, (state, action) => {
                state.isLoadingUpsertPartnerDynamicStages = false;
                Toast.Error({ content: action?.error?.message })
            })
            .addCase(deletePartnerDynamicStage.pending, (state, action) => {
                state.isLoadingDeletePartnerDynamicStages = true;
            })
            .addCase(deletePartnerDynamicStage.fulfilled, (state, action) => {
                state.isLoadingDeletePartnerDynamicStages = false;
                // Toast.Success({ content: t('dynamic_stage_deleted') })
            })
            .addCase(deletePartnerDynamicStage.rejected, (state, action) => {
                state.isLoadingDeletePartnerDynamicStages = false;
                Toast.Error({ content: action?.error?.message })
            })

    }
})

export const {
    handleCompanyProfileModal,
    handleInCompleteCompanyProfileModal
} = appPartnerSlice.actions

export default appPartnerSlice.reducer
